import { useState } from 'react';
import { ChevronDown, ChevronUp, Video, Cpu, Server, Brain, BarChart3, Sparkles, ArrowRight, X, Eye, Clock, Database, GitBranch, Activity, HardDrive } from 'lucide-react';
import LeadCaptureModal from '../../../components/LeadCaptureModal';

export default function PawareVisionVersion_1_0() {
  const [openFaq, setOpenFaq] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);

  const [modalState, setModalState] = useState({
    isOpen: false,
    source: 'sdk',
    buttonType: 'orcamento'
  });

  const openModal = (buttonType = 'orcamento') => {
    setModalState({
      isOpen: true,
      source: 'sdk',
      buttonType
    });
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const components = [
    {
      icon: <Video className="w-8 h-20" />,
      title: "Análise de Vídeos Longos",
      description: "Processe vídeos de qualquer duração com eficiência",
      fullDescription: "Sistema otimizado para análise de vídeos longos com processamento em lotes. Extração inteligente de frames, análise temporal, e suporte para vídeos de horas de duração. Processamento distribuído e gerenciamento eficiente de memória.",
      gradient: "from-black-500 to-pink-500"
    },
    {
      icon: <Eye className="w-8 h-20" />,
      title: "VLM Qwen2-VL",
      description: "Modelo de visão e linguagem de última geração",
      fullDescription: "Integração nativa com Qwen2-VL-7B-Instruct, um dos modelos VLM mais avançados. Suporte para análise multimodal, compreensão de contexto visual e geração de descrições detalhadas de conteúdo em vídeo.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <BarChart3 className="w-8 h-20" />,
      title: "Processamento em Lotes",
      description: "Análise eficiente de múltiplos frames",
      fullDescription: "Sistema inteligente de batching que otimiza o uso de GPU. Processamento paralelo de frames, gestão automática de memória, e capacidade de processar centenas de frames por lote mantendo alta performance.",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: <Brain className="w-8 h-20" />,
      title: "Treinamento Custom",
      description: "Fine-tune com seus próprios dados",
      fullDescription: "Capacidade completa de treinamento e fine-tuning de modelos VLM open source. Suporte para datasets customizados, transfer learning, e otimização para casos de uso específicos com infraestrutura GPU robusta.",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: <Clock className="w-8 h-20" />,
      title: "Análise Temporal",
      description: "Compreensão de sequências e eventos",
      fullDescription: "Análise avançada de sequências temporais em vídeos. Detecção de mudanças de cena, tracking de objetos ao longo do tempo, e compreensão de narrativas e eventos sequenciais.",
      gradient: "from-indigo-500 to-black-500"
    },
    {
      icon: <Activity className="w-8 h-20" />,
      title: "Monitoramento GPU",
      description: "Otimização e controle de recursos",
      fullDescription: "Dashboard completo de monitoramento de GPU em tempo real. Visualização de uso de memória VRAM, temperatura, utilização, e otimização automática de batch size baseada em recursos disponíveis.",
      gradient: "from-pink-500 to-rose-500"
    }
  ];

  const faqs = [
    {
      question: "Quais são os requisitos mínimos de GPU?",
      answer: "Para uso em produção, recomendamos GPUs com no mínimo 24GB de VRAM (RTX 3090, A5000). Para treinamento e fine-tuning, GPUs com 40GB+ de VRAM são ideais (A100, H100). O SDK suporta multi-GPU para escalar conforme necessário."
    },
    {
      question: "Quais formatos de vídeo são suportados?",
      answer: "O SDK suporta todos os principais formatos de vídeo incluindo MP4, AVI, MOV, MKV, WebM, e mais. Utilizamos OpenCV para decodificação, garantindo compatibilidade ampla com codecs modernos."
    },
    {
      question: "Como funciona o processamento em lotes?",
      answer: "O sistema divide automaticamente vídeos longos em lotes de frames otimizados para sua GPU. O tamanho do lote é ajustado dinamicamente baseado na VRAM disponível, maximizando throughput sem causar out-of-memory errors."
    },
    {
      question: "Posso treinar meus próprios modelos VLM?",
      answer: "Sim! O SDK inclui pipeline completo para fine-tuning de modelos VLM open source. Suporte para LoRA, QLoRA, e treinamento completo com gestão automática de checkpoints e métricas."
    }
  ];

  const gpuRequirements = [
    { 
      type: "Desenvolvimento", 
      gpu: "RTX A5000/A6000", 
      vram: "40-48GB", 
      use: "Testes e pequenos vídeos",
      icon: "🔧" 
    },
    {
      type: "Enterprise", 
      gpu: "A100/H100", 
      vram: "40-80GB", 
      use: "Treinamento e alta escala",
      icon: "🚀" 
    },
    { 
      type: "Multi-GPU", 
      gpu: "2x+ A100", 
      vram: "80GB+", 
      use: "Máxima performance",
      icon: "⚡" 
    }
  ];

  const supportedModels = [
    { name: "Qwen2-VL-7B", icon: "", description: "Modelo principal integrado" },
    { name: "Custom VLM", icon: "", description: "Treine seu próprio" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black-900 to-slate-900">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-black-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Component Modal */}
      {selectedComponent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedComponent(null)}>
          <div className="relative max-w-lg w-full bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-white/20 p-8" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedComponent(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className={`w-16 h-16 bg-gradient-to-r ${selectedComponent.gradient} rounded-xl flex items-center justify-center mb-6 text-white`}>
              {selectedComponent.icon}
            </div>
            
            <h3 className="text-2xl font-bold text-white mb-4">{selectedComponent.title}</h3>
            <p className="text-gray-300 leading-relaxed">{selectedComponent.fullDescription}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="relative bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-400">Distribuído por</span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Paware Softwares
              </span>
            </div>
            <button 
              onClick={() => window.location.href = '/'} 
              className="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 hover:border-gray-400 rounded-lg transition-all duration-300 hover:bg-white/10"
            >
              ← Voltar ao Início
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative container mx-auto px-6 pt-20 pb-16">
        <div className="text-center max-w-5xl mx-auto">
          <div className="mb-6 inline-flex items-center px-4 py-2 bg-gradient-to-r from-black-500/20 to-pink-500/20 rounded-full border border-black-400/30">
            <Sparkles className="w-4 h-4 text-white mr-2" />
            <span className="text-sm text-white">Análise Avançada de Vídeos com IA</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-black-200 to-indigo-200 bg-clip-text text-transparent">
            Paware Vision
          </h1>
          <div className="text-3xl font-light text-white mb-8">
            SDK v1.0
          </div>
          
          <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
            SDK completo para análise de vídeos de longa duração com 
            <span className="text-black-400 font-semibold"> modelos VLM avançados</span>, 
            <span className="text-indigo-400 font-semibold"> processamento otimizado em GPU</span> e 
            <span className="text-indigo-400 font-semibold"> capacidade de treinamento custom</span>.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
              <Video className="w-5 h-5 text-indigo-400" />
              <span className="text-white">Vídeos Longos</span>
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
              <Cpu className="w-5 h-5 text-indigo-400" />
              <span className="text-white">GPU Otimizada</span>
            </div>
            <div className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
              <Brain className="w-5 h-5 text-indigo-400" />
              <span className="text-white">VLM Training</span>
            </div>
          </div>

          <button onClick={() => openModal('saiba_mais')} className="group relative px-8 py-4 bg-gradient-to-r from-indigo-400 to-blue-500 rounded-lg font-semibold text-white shadow-2xl hover:shadow-black-500/50 transition-all duration-300 hover:scale-105">
            <span className="flex items-center">
              Solicitar Demonstração
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>
      </section>

      {/* Main SDK Components */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Arquitetura do SDK Vision
            </h2>
            <p className="text-lg md:text-xl text-gray-400">Pipeline completo para análise de vídeo com IA</p>
          </div>

          <div className="flex flex-col lg:flex-row justify-center gap-8 max-w-4xl mx-auto">
            {/* Processing Engine */}
            <div className="group relative flex-1 lg:max-w-md">
              <div className="absolute inset-0 bg-gradient-to-r from-black-600 to-pink-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-black-900/90 to-pink-900/90 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/20 hover:border-white/40 transition-all duration-300 h-full">
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-black-400 to-black-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Server className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Engine</h3>
                    <p className="text-indigo-400 text-base md:text-lg mb-1">Processing Core</p>
                    <p className="text-gray-300 text-xs md:text-sm">PyTorch • CUDA • OpenCV</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-300">
                    <HardDrive className="w-4 h-4 text-black-400 mr-2" />
                    <span>Multi-GPU Support</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-300">
                    <GitBranch className="w-4 h-4 text-pink-400 mr-2" />
                    <span>Batch Processing</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-300">
                    <Database className="w-4 h-4 text-indigo-400 mr-2" />
                    <span>Frame Extraction</span>
                  </div>
                </div>

                <div className="mt-auto pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm text-gray-400">Performance</span>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse animation-delay-200"></div>
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse animation-delay-400"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* VLM Interface */}
            <div className="group relative flex-1 lg:max-w-md">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-black-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-indigo-900/90 to-black-900/90 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-white/20 hover:border-white/40 transition-all duration-300 h-full">
                <div className="flex items-start mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Brain className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2">VLM</h3>
                    <p className="text-indigo-300 text-base md:text-lg mb-1">Vision Models</p>
                    <p className="text-gray-300 text-xs md:text-sm">Qwen2-VL • Custom Models</p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center text-sm text-gray-300">
                    <Eye className="w-4 h-4 text-indigo-400 mr-2" />
                    <span>Visual Understanding</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-300">
                    <Activity className="w-4 h-4 text-black-400 mr-2" />
                    <span>Real-time Analysis</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-300">
                    <Brain className="w-4 h-4 text-pink-400 mr-2" />
                    <span>Fine-tuning Ready</span>
                  </div>
                </div>

                <div className="mt-auto pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm text-gray-400">Model Status</span>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse animation-delay-200"></div>
                      <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse animation-delay-400"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Components Grid */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Componentes Poderosos Incluídos
            </h2>
            <p className="text-xl text-gray-400">Clique em cada componente para mais detalhes</p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-6xl mx-auto">
            {components.map((component, index) => (
              <div
                key={index}
                onClick={() => setSelectedComponent(component)}
                className="group relative cursor-pointer"
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${component.gradient} rounded-2xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-200`}></div>
                <div className="relative bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-white/30 w-40 h-40 flex flex-col items-center justify-center">
                  <div className={`w-16 h-16 bg-gradient-to-r ${component.gradient} rounded-xl flex items-center justify-center mb-3 text-white`}>
                    {component.icon}
                  </div>
                  <h3 className="text-xs font-semibold text-white text-center leading-tight">{component.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GPU Requirements */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Requisitos de GPU
            </h2>
            <p className="text-xl text-gray-400">Escolha a configuração ideal para seu caso de uso</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {gpuRequirements.map((req, index) => (
              <div key={index} className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-black-600 to-pink-600 rounded-xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity"></div>
                <div className="relative bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-white/30">
                  <h3 className="text-lg font-semibold text-white mb-2">{req.type}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-white">GPU: {req.gpu}</p>
                    <p className="text-indigo-400">VRAM: {req.vram}</p>
                    <p className="text-gray-400">{req.use}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Models */}
      <section className="relative py-20 bg-black/20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Modelos VLM Suportados
            </h2>
            <p className="text-xl text-gray-400">Compatibilidade com os principais modelos open source</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {supportedModels.map((model, index) => (
              <div key={index} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-all duration-300">
                <div className="text-4xl mb-3">{model.icon}</div>
                <h4 className="text-white font-semibold mb-2">{model.name}</h4>
                <p className="text-gray-400 text-sm">{model.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Perguntas Frequentes
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-colors">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between"
                >
                  <span className="font-medium text-white">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Footer */}
      <footer className="relative bg-black/50 backdrop-blur-xl border-t border-white/10 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-400">© 2025 Paware Vision SDK v1.0</p>
              <p className="text-sm text-gray-500">
                Distribuído por <span className="text-blue-400 font-semibold">Paware Softwares</span>
              </p>
            </div>
            <div className="text-sm text-gray-400 text-center md:text-right">
              <p>Licença permite edição completa do código</p>
              <p className="text-xs text-gray-500 mt-1">Uso comercial do SDK modificado não permitido</p>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        .animation-delay-400 {
          animation-delay: 400ms;
        }
      `}</style>
      <LeadCaptureModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        source={modalState.source}
        buttonType={modalState.buttonType}
      />
    </div>
  );
}